import { Stars, RATING_KEYS, RATING_LABELS } from "./Ratings";
import { BadgeCheck, CheckCircle2, MessageSquare, PencilLine, Building2, AlertCircle, Wrench } from "lucide-react";
import { API } from "../lib/api";

const RESPONSIBLE = { landlord: "Landlord", letting_agent: "Letting Agent" };

const Tip = ({ children, text, testid }) => (
  <span className="relative inline-flex group" data-testid={testid}>
    {children}
    <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-60 bg-navy text-white text-xs rounded-lg px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity z-20 leading-relaxed">
      {text}
    </span>
  </span>
);

export const ReviewCard = ({ review }) => (
  <div className="bg-white border border-slate-200 rounded-2xl p-6 flex flex-col gap-4 shadow-sm" data-testid={`review-card-${review.id}`}>
    <div className="flex items-start justify-between gap-3 flex-wrap">
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-navy">{review.tenant_name}</span>
          <Tip testid={`verified-tip-${review.id}`} text="We confirmed this person held a tenancy at this address (they provided a tenancy agreement). Their ratings are their own honest opinion.">
            <span className="bg-mint-soft text-mint-dark px-2 py-0.5 text-xs flex items-center gap-1 rounded-md font-semibold cursor-help">
              <BadgeCheck className="w-3.5 h-3.5" /> Verified Tenant
            </span>
          </Tip>
          {review.edited && (
            <Tip testid={`edited-tip-${review.id}`} text="The Tenverly team lightly edited this review to keep it in line with our guidelines. Nothing was added and its substance was not changed.">
              <span className="bg-slate-100 text-slate-600 px-2 py-0.5 text-xs flex items-center gap-1 rounded-md font-semibold cursor-help" data-testid={`edited-badge-${review.id}`}>
                <PencilLine className="w-3.5 h-3.5" /> Edited
              </span>
            </Tip>
          )}
          {review.resolved && (
            <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 text-xs font-semibold flex items-center gap-1 rounded-md" data-testid={`resolved-badge-${review.id}`}>
              <CheckCircle2 className="w-3.5 h-3.5" /> Resolved
            </span>
          )}
          {review.disputed && (
            <span className="bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 text-xs font-semibold flex items-center gap-1 rounded-md" data-testid={`disputed-badge-${review.id}`}>
              <AlertCircle className="w-3.5 h-3.5" /> Under review
            </span>
          )}
        </div>
        <div className="mt-1 flex items-center gap-3 flex-wrap text-xs text-slate-400 font-mono">
          {(review.tenancy_start || review.tenancy_end) && (
            <span>Tenancy: {review.tenancy_start || "?"} → {review.tenancy_end || "present"}</span>
          )}
          {review.responsible_party && (
            <span className="flex items-center gap-1 text-slate-500" data-testid={`responsible-${review.id}`}>
              <Building2 className="w-3.5 h-3.5" /> Managed by: {RESPONSIBLE[review.responsible_party]}
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Stars value={review.overall} />
        <span className="font-mono font-bold text-navy">{review.overall.toFixed(1)}</span>
      </div>
    </div>

    {review.title && <h4 className="font-semibold font-heading text-lg text-navy">{review.title}</h4>}
    <p className="text-slate-600 leading-relaxed whitespace-pre-line">{review.body}</p>

    <div className="grid grid-cols-2 gap-x-6 gap-y-2 pt-3 border-t border-slate-100">
      {RATING_KEYS.map((k) => (
        <div key={k} className="flex items-center justify-between text-sm">
          <span className="text-slate-500">{RATING_LABELS[k]}</span>
          <Stars value={review.ratings[k]} size="w-3.5 h-3.5" />
        </div>
      ))}
    </div>

    {review.landlord_reply && (
      <div className="border-l-4 border-mint bg-slate-50 p-4 mt-1 rounded-r-xl" data-testid={`landlord-reply-${review.id}`}>
        <div className="flex items-center gap-2 text-sm font-semibold text-navy">
          <MessageSquare className="w-4 h-4 text-mint-dark" /> Landlord response · {review.landlord_reply.author}
        </div>
        <p className="mt-1 text-slate-600">{review.landlord_reply.text}</p>
      </div>
    )}

    {review.resolved && review.proof_of_work_file_id && (
      <div className="border-l-4 border-emerald-500 bg-emerald-50/60 p-4 mt-1 rounded-r-xl" data-testid={`proof-of-work-${review.id}`}>
        <div className="flex items-center gap-2 text-sm font-semibold text-emerald-800">
          <Wrench className="w-4 h-4" /> Landlord's proof of work
        </div>
        <p className="mt-1 text-sm text-emerald-700/90">The landlord uploaded evidence that this issue was resolved.</p>
        <a href={`${API}/reviews/${review.id}/proof`} target="_blank" rel="noopener noreferrer" data-testid={`proof-view-${review.id}`} className="mt-3 block w-fit">
          <img src={`${API}/reviews/${review.id}/proof`} alt="Proof of work"
            className="max-h-56 rounded-lg border border-emerald-200 object-cover hover:opacity-90 transition-opacity"
            onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.insertAdjacentHTML("afterend", '<span class="text-sm font-semibold text-emerald-700 underline">View uploaded document →</span>'); }} />
        </a>
      </div>
    )}
  </div>
);
